import { c as _c } from "react/compiler-runtime";
import { useMutation } from '@apollo/client/react';
import { Badge, Box, Button, HStack, Input, InputGroup, Spinner, Text, VStack, chakra } from '@chakra-ui/react';
import { AutoResizeTextarea } from '@src/components/sco/AutoResizeTextarea';
import { CommentPendingAttachments, useCommentAttachments } from '@src/components/sco/Comments/CommentAttachments';
import { CurrencyInput } from '@src/components/sco/CurrencyInput';
import { LocationInput, type LocationInputPlace } from '@src/components/sco/LocationInput';
import { useScoChatEmbedContext, type ScoQuestionAnswerPayload } from '@src/components/sco/MarkdownRenderer/ScoChatEmbedContext';
import { ScoQuestionTagsField } from '@src/components/sco/MarkdownRenderer/ScoQuestionTagsField';
import { useCurrentUser } from '@src/hooks/useCurrentUser';
import { gql } from '@src/lib/gql';
import { localStore } from '@src/lib/store';
import { parseAsString, useQueryState } from 'nuqs';
import { Children, isValidElement, type KeyboardEvent as ReactKeyboardEvent, type ReactNode, useCallback, useMemo, useRef, useState } from 'react';
import { LuPaperclip } from 'react-icons/lu';
export type ScoOptionProps = {
  id?: string;
  label?: string;
  'allow-free-text'?: string;
  children?: ReactNode;
};

/** Presentational option leaf — props feed parent ScoQuestion via children walk. */
export const ScoOption = (_props: ScoOptionProps) => {
  return null;
};
export type ScoQuestionProps = {
  'question-id'?: string;
  'field-type'?: string;
  selection?: string;
  ui?: string;
  prompt?: string;
  'submit-label'?: string;
  placeholder?: string;
  currency?: string;
  'tag-type-keys'?: string;
  accept?: string;
  'max-files'?: string;
  children?: ReactNode;
};
type OptionItem = {
  id: string;
  label: string;
  allowFreeText?: boolean;
};

/**
 * Known catalog keys → presentation chrome.
 * Prefer ambient `colorPalette` (inherit from chat) — do not hardcode purple/blue/etc.
 */
export type QuestionPresentation = {
  variant?: 'chips' | 'cards' | 'default';
  badge?: string;
};
export const QUESTION_PRESENTATION: Record<string, QuestionPresentation> = {
  'company-stage': {
    variant: 'chips',
    badge: 'Stage'
  },
  stage: {
    variant: 'chips',
    badge: 'Stage'
  },
  'primary-need': {
    variant: 'chips',
    badge: 'Needs'
  },
  email: {
    variant: 'default',
    badge: 'Account'
  },
  'funding-goal': {
    variant: 'default',
    badge: 'Funding'
  }
};

/**
 * Shared card chrome — colorPalette.* + sizing aligned to chat-message body
 * (MarkdownRenderer baseFontSize ~1em / 0.95em). Avoid oversized sm/default
 * controls that fight the surrounding prose.
 */
const openCardStyle = {
  borderColor: 'colorPalette.border',
  bg: 'colorPalette.subtle'
} as const;
const answeredCardStyle = {
  borderColor: 'colorPalette.border/40',
  bg: 'colorPalette.muted/25'
} as const;

/** Chip / option buttons — body-adjacent, not default Button sm. */
const chipButtonProps = {
  size: 'xs' as const,
  borderRadius: 'full' as const,
  borderWidth: '1px' as const,
  borderColor: 'colorPalette.border',
  height: 'auto' as const,
  minH: '1.75rem',
  px: 2.5,
  py: 1,
  fontSize: '0.95em',
  fontWeight: 'medium' as const,
  lineHeight: '1.3'
};
const FIELD_TYPES = new Set(['single-select', 'multi-select', 'text', 'textarea', 'currency', 'number', 'tags', 'location', 'file', 'email']);
function collectOptions(children: ReactNode): OptionItem[] {
  const options: OptionItem[] = [];
  Children.forEach(children, child => {
    if (!isValidElement(child)) {
      return;
    }
    const props = child.props as ScoOptionProps & {
      id?: string;
      label?: string;
    };
    const id = (props.id || props['id'] || '').toString().trim().toLowerCase();
    if (!id) {
      return;
    }
    const label = (props.label || props['label'] || id).toString().trim();
    const allowFreeText = props['allow-free-text'] === 'true' || id === 'other';
    options.push({
      id,
      label,
      allowFreeText
    });
  });
  return options;
}
function normalizeFieldType(props: ScoQuestionProps): string {
  let fieldType = (props['field-type'] || '').toLowerCase().trim();
  if (!fieldType && props.selection) {
    const sel = props.selection.toLowerCase();
    if (sel === 'single') {
      fieldType = 'single-select';
    } else if (sel === 'multiple' || sel === 'multi') {
      fieldType = 'multi-select';
    }
  }
  if (!fieldType) {
    fieldType = 'single-select';
  }
  if (!FIELD_TYPES.has(fieldType)) {
    fieldType = 'text';
  }
  return fieldType;
}
function promptPrefix(prompt: string) {
  return prompt.replace(/\?$/, '').trim();
}

/** Enter submits; Shift+Enter inserts newline (for textarea / multi-line). */
function onEnterSubmit(e: ReactKeyboardEvent, opts: {
  canSubmit: boolean;
  onSubmit: () => void;
  allowShiftNewline?: boolean;
}) {
  if (e.key !== 'Enter') {
    return;
  }
  if (opts.allowShiftNewline && e.shiftKey) {
    return;
  }
  // Avoid submitting while composing IME text
  if (e.nativeEvent.isComposing) {
    return;
  }
  if (!opts.canSubmit) {
    return;
  }
  e.preventDefault();
  opts.onSubmit();
}

/**
 * Tag / multi-value answers store titles in valueJson (string[]). Prefer that over
 * the joined `value` string so answered badges match combobox selections.
 */
function tagTitlesFromAnswer(answer?: {
  valueJson?: unknown;
  value?: string | number | null;
} | null): string[] | null {
  const vj = answer?.valueJson;
  if (Array.isArray(vj)) {
    const titles = vj.map(x => x == null ? '' : String(x).trim()).filter(Boolean);
    return titles.length ? titles : null;
  }
  return null;
}

/**
 * Answer-only labels for the answered card badges.
 * Never include the question/prompt — the card already shows `{prompt}` above.
 * `localSummary` / prose often look like "What stage…: Growth"; strip that prefix when present.
 */
function formatAnswerDisplay(opts: {
  answer?: {
    optionIds?: string[];
    optionLabels?: string[];
    value?: string | number | null;
    valueJson?: unknown;
    freeText?: string;
    fieldType?: string;
  } | null;
  localSummary?: string | null;
  labelsFor: (ids: string[]) => string[];
  prompt?: string;
  /** Local tag titles when embeds lag behind submit (same shape as combobox selection). */
  localTagTitles?: string[];
}): string[] {
  const {
    answer,
    localSummary,
    labelsFor,
    prompt,
    localTagTitles
  } = opts;
  if (answer?.optionLabels?.length) {
    const labels = [...answer.optionLabels];
    if (answer.freeText) {
      labels.push(answer.freeText);
    }
    return labels;
  }
  if (answer?.optionIds?.length) {
    const labels = labelsFor(answer.optionIds);
    if (answer.freeText) {
      labels.push(answer.freeText);
    }
    return labels;
  }
  const fromJson = tagTitlesFromAnswer(answer);
  if (fromJson?.length) {
    return answer?.freeText ? [...fromJson, answer.freeText] : fromJson;
  }
  if (localTagTitles?.length) {
    return localTagTitles;
  }
  if (answer?.value != null && answer.value !== '') {
    return answer.freeText ? [`${answer.value}`, answer.freeText] : [String(answer.value)];
  }
  if (answer?.freeText) {
    return [answer.freeText];
  }
  if (localSummary) {
    // Prefer splitting "A, B, C" local tag summaries into badges when possible
    const stripped = stripPromptPrefixFromSummary(localSummary, prompt);
    if (stripped.includes(', ')) {
      return stripped.split(', ').map(s => s.trim()).filter(Boolean);
    }
    return [stripped];
  }
  return ['Answered'];
}

/** Drop leading "Prompt text: " when summary was built as prose for the model. */
function stripPromptPrefixFromSummary(summary: string, prompt?: string): string {
  const trimmed = summary.trim();
  if (!trimmed) {
    return trimmed;
  }
  if (prompt) {
    const prefix = `${promptPrefix(prompt)}: `;
    if (trimmed.startsWith(prefix)) {
      return trimmed.slice(prefix.length).trim() || trimmed;
    }
  }
  return trimmed;
}

/** Short human labels for local answered UI (not the model-facing proseSummary). */
function answerOnlySummary(payload: ScoQuestionAnswerPayload, prompt?: string): string | null {
  if (payload.optionLabels?.length) {
    const labels = [...payload.optionLabels];
    if (payload.freeText) {
      labels.push(payload.freeText);
    }
    return labels.join(', ');
  }
  if (payload.value != null && payload.value !== '') {
    return payload.freeText ? `${payload.value} — ${payload.freeText}` : String(payload.value);
  }
  if (payload.freeText) {
    return payload.freeText;
  }
  const prose = (payload.proseSummary || '').trim();
  return prose ? stripPromptPrefixFromSummary(prose, prompt) : null;
}

/**
 * Run `work`; if it throws or rejects, run `recover` with the error instead.
 * Kept outside the component so the try/catch doesn't stop React Compiler
 * from compiling `ScoQuestion`.
 */
async function runOrRecover(work: () => Promise<void>, recover: (err: unknown) => Promise<void>): Promise<void> {
  try {
    await work();
  } catch (err) {
    await recover(err);
  }
}

/**
 * Interactive in-chat question card (sco-question embed).
 * Choice chips are local state only (no SelectableButtons) to avoid controlled-value loops.
 */
export const ScoQuestion = props => {
  const $ = _c(155);
  const questionId = (props["question-id"] || "").trim().toLowerCase();
  const fieldType = normalizeFieldType(props);
  const prompt = props.prompt || "Choose an option";
  const ui = (props.ui || "").toLowerCase() || (fieldType === "multi-select" ? "form" : "buttons");
  const submitLabel = props["submit-label"] || "Continue";
  const placeholder = props.placeholder || "";
  const currency = (props.currency || "USD").toUpperCase();
  const t0 = props["tag-type-keys"] || "industry";
  let t1;
  if ($[0] !== t0) {
    t1 = t0.split(",").map(_temp).filter(Boolean);
    $[0] = t0;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  const tagTypeKeys = t1;
  const maxFiles = Math.max(1, parseInt(props["max-files"] || "5", 10) || 5);
  const t2 = props.accept || "";
  let acceptRaw;
  let t3;
  if ($[2] !== t2) {
    acceptRaw = t2.trim();
    t3 = acceptRaw ? acceptRaw.split(",").map(_temp2).filter(Boolean) : ["*"];
    $[2] = t2;
    $[3] = acceptRaw;
    $[4] = t3;
  } else {
    acceptRaw = $[3];
    t3 = $[4];
  }
  const allowedExtensions = t3;
  let t4;
  if ($[5] !== props.children) {
    t4 = collectOptions(props.children);
    $[5] = props.children;
    $[6] = t4;
  } else {
    t4 = $[6];
  }
  const options = t4;
  const presentation = QUESTION_PRESENTATION[questionId];
  const embedCtx = useScoChatEmbedContext();
  const state = questionId ? embedCtx.getQuestionState?.(questionId) : undefined;
  const answered = !!state?.answered;
  const answer = state?.answer;
  const progress = questionId ? embedCtx.getQuestionProgress?.(questionId) : undefined;
  const {
    currentUser,
    refetchCurrentUser
  } = useCurrentUser();
  const [, setPrompt] = useQueryState("prompt", parseAsString);
  const [, setEmailQuery] = useQueryState("email", parseAsString);
  let t5;
  if ($[7] === Symbol.for("react.memo_cache_sentinel")) {
    t5 = gql`
    mutation ScoQuestionRegisterUser($input: RegisterUserInput!) {
      registerUser(input: $input) {
        loginTokenSent
        viewer {
          id
          currentUser {
            id
            userId
            isRegistered
            isGuest
            email
          }
        }
      }
    }
  `;
    $[7] = t5;
  } else {
    t5 = $[7];
  }
  const [registerUser] = useMutation(t5);
  let t6;
  if ($[8] === Symbol.for("react.memo_cache_sentinel")) {
    t6 = [];
    $[8] = t6;
  } else {
    t6 = $[8];
  }
  const [selected, setSelected] = useState(t6);
  const [freeText, setFreeText] = useState("");
  const [textValue, setTextValue] = useState("");
  const [numberValue, setNumberValue] = useState("");
  const [currencyValue, setCurrencyValue] = useState(0);
  let t7;
  if ($[9] === Symbol.for("react.memo_cache_sentinel")) {
    t7 = [];
    $[9] = t7;
  } else {
    t7 = $[9];
  }
  const [tagTitles, setTagTitles] = useState(t7);
  const [locationPlace, setLocationPlace] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [localAnswered, setLocalAnswered] = useState(false);
  const [localSummary, setLocalSummary] = useState(null);
  const [signupHint, setSignupHint] = useState(null);
  const fileInputRef = useRef(null);
  const t8 = fieldType === "file";
  let t9;
  if ($[10] !== allowedExtensions || $[11] !== maxFiles || $[12] !== t8) {
    t9 = {
      maxFiles,
      allowedExtensions,
      bypassAuth: t8
    };
    $[10] = allowedExtensions;
    $[11] = maxFiles;
    $[12] = t8;
    $[13] = t9;
  } else {
    t9 = $[13];
  }
  const {
    pendingFiles,
    addFile,
    removeFile,
    isUploading,
    isFull,
    storedFileIds
  } = useCommentAttachments(t9);
  const isChoice = fieldType === "single-select" || fieldType === "multi-select";
  const isMulti = fieldType === "multi-select";
  let t10;
  if ($[14] !== options) {
    t10 = options.some(_temp3);
    $[14] = options;
    $[15] = t10;
  } else {
    t10 = $[15];
  }
  const hasOtherOption = t10;
  let t11;
  if ($[16] !== options || $[17] !== selected) {
    let t12;
    if ($[19] !== options) {
      t12 = id => options.find(o_0 => o_0.id === id)?.allowFreeText;
      $[19] = options;
      $[20] = t12;
    } else {
      t12 = $[20];
    }
    t11 = selected.some(t12);
    $[16] = options;
    $[17] = selected;
    $[18] = t11;
  } else {
    t11 = $[18];
  }
  const otherSelected = t11;
  const isInstant = isChoice && !isMulti && ui === "buttons" && !hasOtherOption;
  const questionReady = questionId ? embedCtx.isQuestionReady ? embedCtx.isQuestionReady(questionId) : true : false;
  const canSubmit = embedCtx.canSubmit !== false && !!embedCtx.postAnswer && questionReady;
  const showAnswered = answered || localAnswered;
  let t12;
  if ($[21] !== options) {
    t12 = ids => ids.map(id_0 => options.find(o_1 => o_1.id === id_0)?.label || id_0);
    $[21] = options;
    $[22] = t12;
  } else {
    t12 = $[22];
  }
  const labelsFor = t12;
  let t13;
  if ($[23] !== currentUser?.isRegistered || $[24] !== refetchCurrentUser || $[25] !== registerUser || $[26] !== setEmailQuery || $[27] !== setPrompt) {
    t13 = async email => {
      if (currentUser?.isRegistered) {
        return;
      }
      const signupRequestId = localStore.get("sco:signupRequestId") || undefined;
      await runOrRecover(async () => {
        const res = await registerUser({
          variables: {
            input: {
              email,
              sendConfirmationRequest: true,
              signupRequestId: signupRequestId || undefined
            }
          }
        });
        const payload = res.data?.registerUser;
        if (payload?.loginTokenSent) {
          setSignupHint("We sent a login link to that email \u2014 check your inbox to continue.");
          await setEmailQuery(email);
          await setPrompt("register");
          return;
        }
        await refetchCurrentUser?.();
        setSignupHint("You\u2019re signed in \u2014 keep chatting or tap Get Started when ready.");
      }, async err => {
        console.error("[ScoQuestion] registerUser failed, opening register form", err);
        await setEmailQuery(email);
        await setPrompt("register");
      });
    };
    $[23] = currentUser?.isRegistered;
    $[24] = refetchCurrentUser;
    $[25] = registerUser;
    $[26] = setEmailQuery;
    $[27] = setPrompt;
    $[28] = t13;
  } else {
    t13 = $[28];
  }
  const completeEmailSignup = t13;
  let t14;
  if ($[29] !== completeEmailSignup || $[30] !== currentUser?.isRegistered || $[31] !== embedCtx || $[32] !== prompt || $[33] !== submitting) {
    t14 = async payload_0 => {
      if (!embedCtx.postAnswer || submitting) {
        return;
      }
      setSubmitting(true);
      setSignupHint(null);
      await runOrRecover(async () => {
        await embedCtx.postAnswer(payload_0);
        setLocalAnswered(true);
        setLocalSummary(answerOnlySummary(payload_0, prompt));
        if (payload_0.fieldType === "email" && payload_0.value && !currentUser?.isRegistered) {
          await completeEmailSignup(String(payload_0.value));
        }
      }, _temp4);
      setSubmitting(false);
    };
    $[29] = completeEmailSignup;
    $[30] = currentUser?.isRegistered;
    $[31] = embedCtx;
    $[32] = prompt;
    $[33] = submitting;
    $[34] = t14;
  } else {
    t14 = $[34];
  }
  const post = t14;
  const toggleOption = optionId => {
    if (!canSubmit || submitting || showAnswered) {
      return;
    }
    setSelected(prev => {
      if (isMulti) {
        return prev.includes(optionId) ? prev.filter(id_1 => id_1 !== optionId) : [...prev, optionId];
      }
      return prev.length === 1 && prev[0] === optionId ? [] : [optionId];
    });
  };
  const handleInstant = async optionId_0 => {
    if (!canSubmit || showAnswered) {
      return;
    }
    const opt = options.find(o_2 => o_2.id === optionId_0);
    if (opt?.allowFreeText) {
      setSelected([optionId_0]);
      return;
    }
    const label = opt?.label || optionId_0;
    await post({
      questionId,
      fieldType,
      optionIds: [optionId_0],
      optionLabels: [label],
      proseSummary: `${promptPrefix(prompt)}: ${label}`
    });
  };
  const handleChoiceSubmit = async () => {
    if (!canSubmit || showAnswered || !selected.length) {
      return;
    }
    if (otherSelected && !freeText.trim()) {
      return;
    }
    const labels = labelsFor(selected);
    await post({
      questionId,
      fieldType,
      optionIds: selected,
      optionLabels: labels,
      freeText: otherSelected ? freeText.trim() : undefined,
      proseSummary: otherSelected ? `${promptPrefix(prompt)}: ${labels.join(", ")} — ${freeText.trim()}` : `${promptPrefix(prompt)}: ${labels.join(", ")}`
    });
  };
  const handleValueSubmit = async () => {
    if (!canSubmit || showAnswered) {
      return;
    }
    if (fieldType === "text" || fieldType === "textarea" || fieldType === "email") {
      const v = textValue.trim();
      if (!v) {
        return;
      }
      if (fieldType === "email" && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(v)) {
        return;
      }
      await post({
        questionId,
        fieldType,
        value: v,
        proseSummary: `${promptPrefix(prompt)}: ${v}`
      });
      return;
    }
    if (fieldType === "number") {
      const n = numberValue.trim();
      if (n === "" || Number.isNaN(Number(n))) {
        return;
      }
      await post({
        questionId,
        fieldType,
        value: Number(n),
        proseSummary: `${promptPrefix(prompt)}: ${n}`
      });
      return;
    }
    if (fieldType === "currency") {
      if (!currencyValue || currencyValue <= 0) {
        return;
      }
      await post({
        questionId,
        fieldType,
        value: currencyValue,
        currency,
        proseSummary: `${promptPrefix(prompt)}: ${currency} ${currencyValue.toLocaleString()}`
      });
      return;
    }
    if (fieldType === "tags") {
      if (!tagTitles.length) {
        return;
      }
      await post({
        questionId,
        fieldType,
        value: tagTitles.join(", "),
        valueJson: tagTitles,
        proseSummary: `${promptPrefix(prompt)}: ${tagTitles.join(", ")}`
      });
      return;
    }
    if (fieldType === "location") {
      if (!locationPlace?.title) {
        return;
      }
      await post({
        questionId,
        fieldType,
        value: locationPlace.title,
        valueJson: locationPlace,
        proseSummary: `${promptPrefix(prompt)}: ${locationPlace.title}`
      });
      return;
    }
    if (fieldType === "file") {
      if (!storedFileIds.length || isUploading) {
        return;
      }
      const names = pendingFiles.map(_temp5).filter(Boolean);
      await post({
        questionId,
        fieldType,
        storedFileIds: [...storedFileIds],
        value: names.join(", ") || `${storedFileIds.length} file(s)`,
        proseSummary: `${promptPrefix(prompt)}: ${names.join(", ") || `${storedFileIds.length} file(s)`}`
      });
    }
  };
  if (isChoice && options.length < 2 && !showAnswered) {
    return null;
  }
  if (!questionId && !showAnswered) {
    return null;
  }
  let t15;
  if ($[35] === Symbol.for("react.memo_cache_sentinel")) {
    t15 = {
      mt: 2,
      mb: 1,
      px: 2.5,
      py: 2,
      borderWidth: "1px" as const,
      borderRadius: "md" as const,
      maxW: "full" as const,
      fontSize: "1em" as const,
      lineHeight: "1.5" as const
    };
    $[35] = t15;
  } else {
    t15 = $[35];
  }
  const cardProps = t15;
  let t16;
  if ($[36] !== presentation) {
    t16 = presentation?.badge && <Badge flexShrink={0} fontSize="0.75em" size="sm" variant="subtle">{presentation.badge}</Badge>;
    $[36] = presentation;
    $[37] = t16;
  } else {
    t16 = $[37];
  }
  let t17;
  if ($[38] !== prompt) {
    t17 = <Text color="colorPalette.fg" fontSize="1em" fontWeight="semibold" lineClamp={3} lineHeight="1.5">{prompt}</Text>;
    $[38] = prompt;
    $[39] = t17;
  } else {
    t17 = $[39];
  }
  let t18;
  if ($[40] !== t16 || $[41] !== t17) {
    t18 = <HStack align="center" flex={1} gap={1.5} minW={0}>{t16}{t17}</HStack>;
    $[40] = t16;
    $[41] = t17;
    $[42] = t18;
  } else {
    t18 = $[42];
  }
  const header = <HStack align="start" gap={2} justify="space-between" mb={1.5} wrap="wrap">{t18}{progress && progress.total > 1 && <Text color="colorPalette.fg/60" flexShrink={0} fontSize="0.85em" lineHeight="1.5">{progress.index} of {progress.total}</Text>}</HStack>;
  if (showAnswered) {
    const displayLabels = formatAnswerDisplay({
      answer,
      localSummary,
      labelsFor,
      prompt,
      localTagTitles: fieldType === "tags" && tagTitles.length ? tagTitles : undefined
    });
    let t19;
    if ($[43] !== prompt) {
      t19 = <Text color="colorPalette.fg/70" fontSize="0.95em" lineHeight="1.5" mb={1}>{prompt}</Text>;
      $[43] = prompt;
      $[44] = t19;
    } else {
      t19 = $[44];
    }
    let t20;
    if ($[45] !== signupHint) {
      t20 = signupHint && <Text color="colorPalette.fg/60" fontSize="0.9em" lineHeight="1.4" mt={1.5}>{signupHint}</Text>;
      $[45] = signupHint;
      $[46] = t20;
    } else {
      t20 = $[46];
    }
    let t21;
    if ($[47] !== props.children) {
      t21 = <chakra.span display="none">{props.children}</chakra.span>;
      $[47] = props.children;
      $[48] = t21;
    } else {
      t21 = $[48];
    }
    return <Box {...cardProps} {...answeredCardStyle} opacity={0.95}>{t19}<HStack gap={1.5} wrap="wrap">{displayLabels.map(_temp6)}</HStack>{t20}{t21}</Box>;
  }
  if (isChoice) {
    const showFreeText = otherSelected;
    let t19;
    if ($[49] !== freeText || $[50] !== otherSelected) {
      t19 = otherSelected && !freeText.trim();
      $[49] = freeText;
      $[50] = otherSelected;
      $[51] = t19;
    } else {
      t19 = $[51];
    }
    const freeTextRequired = t19;
    const canSubmitChoice = canSubmit && !submitting && selected.length > 0 && !freeTextRequired;
    const showContinue = isMulti || ui === "form" || hasOtherOption || selected.length > 0;
    let t20;
    if ($[52] !== props.children) {
      t20 = <chakra.span display="none">{props.children}</chakra.span>;
      $[52] = props.children;
      $[53] = t20;
    } else {
      t20 = $[53];
    }
    return <Box {...cardProps} {...openCardStyle}>{header}{isInstant ? <HStack gap={1.5} wrap="wrap">{options.map(opt_0 => <Button key={opt_0.id} {...chipButtonProps} bg="colorPalette.emphasized/20" color="colorPalette.fg" disabled={!canSubmit || submitting} variant="outline" _hover={{
          bg: "colorPalette.emphasized/40",
          borderColor: "colorPalette.border"
        }} onClick={() => void handleInstant(opt_0.id)}>{opt_0.label}</Button>)}{submitting && <Spinner color="colorPalette.fg" size="xs" />}</HStack> : <VStack align="stretch" gap={2}><HStack gap={1.5} wrap="wrap">{options.map(opt_1 => {
            const isSel = selected.includes(opt_1.id);
            return <Button key={opt_1.id} {...chipButtonProps} aria-pressed={isSel} bg={isSel ? "colorPalette.emphasized" : "colorPalette.emphasized/20"} color={isSel ? "colorPalette.contrast" : "colorPalette.fg"} disabled={!canSubmit || submitting} variant="outline" _hover={{
              bg: isSel ? "colorPalette.emphasized" : "colorPalette.emphasized/40",
              borderColor: "colorPalette.border"
            }} onClick={() => {
              if (!isMulti && ui === "buttons" && !opt_1.allowFreeText && !hasOtherOption) {
                handleInstant(opt_1.id);
                return;
              }
              toggleOption(opt_1.id);
            }}>{opt_1.label}</Button>;
          })}</HStack>{showFreeText && <AutoResizeTextarea disabled={!canSubmit || submitting} fontSize="1em" minH="2.25rem" placeholder={placeholder || "Please specify\u2026"} size="sm" value={freeText} onChange={e_0 => setFreeText(e_0.target.value)} onKeyDown={e_1 => onEnterSubmit(e_1, {
          canSubmit: canSubmitChoice,
          onSubmit: () => void handleChoiceSubmit(),
          allowShiftNewline: true
        })} />}{showContinue && <HStack><Button {...chipButtonProps} _hover={{
            bg: "colorPalette.emphasized/90"
          }} bg="colorPalette.emphasized" borderColor="colorPalette.emphasized" borderRadius="md" color="colorPalette.contrast" disabled={!canSubmitChoice} variant="solid" onClick={() => void handleChoiceSubmit()}>{submitting ? <Spinner size="xs" /> : submitLabel}</Button></HStack>}</VStack>}{t20}</Box>;
  }
  let t19;
  bb0: {
    if (!canSubmit || submitting) {
      t19 = false;
      break bb0;
    }
    switch (fieldType) {
      case "text":
      case "textarea":
        {
          let t20;
          if ($[54] !== textValue) {
            t20 = textValue.trim();
            $[54] = textValue;
            $[55] = t20;
          } else {
            t20 = $[55];
          }
          t19 = t20.length > 0;
          break bb0;
        }
      case "email":
        {
          let t20;
          if ($[56] === Symbol.for("react.memo_cache_sentinel")) {
            t20 = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
            $[56] = t20;
          } else {
            t20 = $[56];
          }
          let t21;
          if ($[57] !== textValue) {
            t21 = t20.test(textValue.trim());
            $[57] = textValue;
            $[58] = t21;
          } else {
            t21 = $[58];
          }
          t19 = t21;
          break bb0;
        }
      case "number":
        {
          let t20;
          if ($[59] !== numberValue) {
            t20 = numberValue.trim() !== "" && !Number.isNaN(Number(numberValue));
            $[59] = numberValue;
            $[60] = t20;
          } else {
            t20 = $[60];
          }
          t19 = t20;
          break bb0;
        }
      case "currency":
        {
          t19 = !!currencyValue && currencyValue > 0;
          break bb0;
        }
      case "tags":
        {
          t19 = tagTitles.length > 0;
          break bb0;
        }
      case "location":
        {
          t19 = !!locationPlace?.title;
          break bb0;
        }
      case "file":
        {
          t19 = storedFileIds.length > 0 && !isUploading;
          break bb0;
        }
      default:
        {
          let t20;
          if ($[61] !== textValue) {
            t20 = textValue.trim();
            $[61] = textValue;
            $[62] = t20;
          } else {
            t20 = $[62];
          }
          t19 = t20.length > 0;
        }
    }
  }
  const valueCanSubmit = t19;
  let t20;
  if ($[63] !== canSubmit || $[64] !== fieldType || $[65] !== handleValueSubmit || $[66] !== placeholder || $[67] !== setTextValue || $[68] !== submitting || $[69] !== textValue || $[70] !== valueCanSubmit) {
    t20 = fieldType === "text" && <Input disabled={!canSubmit || submitting} fontSize="1em" placeholder={placeholder || "Type your answer\u2026"} size="sm" type="text" value={textValue} onChange={e_2 => setTextValue(e_2.target.value)} onKeyDown={e_3 => onEnterSubmit(e_3, {
      canSubmit: valueCanSubmit,
      onSubmit: () => void handleValueSubmit()
    })} />;
    $[63] = canSubmit;
    $[64] = fieldType;
    $[65] = handleValueSubmit;
    $[66] = placeholder;
    $[67] = setTextValue;
    $[68] = submitting;
    $[69] = textValue;
    $[70] = valueCanSubmit;
    $[71] = t20;
  } else {
    t20 = $[71];
  }
  let t21;
  if ($[72] !== canSubmit || $[73] !== fieldType || $[74] !== handleValueSubmit || $[75] !== placeholder || $[76] !== setTextValue || $[77] !== submitting || $[78] !== textValue || $[79] !== valueCanSubmit) {
    t21 = fieldType === "textarea" && <AutoResizeTextarea disabled={!canSubmit || submitting} fontSize="1em" minH="3.5rem" placeholder={placeholder || "Type your answer\u2026 (Enter to submit, Shift+Enter for newline)"} size="sm" value={textValue} onChange={e_4 => setTextValue(e_4.target.value)} onKeyDown={e_5 => onEnterSubmit(e_5, {
      canSubmit: valueCanSubmit,
      onSubmit: () => void handleValueSubmit(),
      allowShiftNewline: true
    })} />;
    $[72] = canSubmit;
    $[73] = fieldType;
    $[74] = handleValueSubmit;
    $[75] = placeholder;
    $[76] = setTextValue;
    $[77] = submitting;
    $[78] = textValue;
    $[79] = valueCanSubmit;
    $[80] = t21;
  } else {
    t21 = $[80];
  }
  let t22;
  if ($[81] !== canSubmit || $[82] !== fieldType || $[83] !== handleValueSubmit || $[84] !== placeholder || $[85] !== setTextValue || $[86] !== submitting || $[87] !== textValue || $[88] !== valueCanSubmit) {
    t22 = fieldType === "email" && <Input autoComplete="email" disabled={!canSubmit || submitting} fontSize="1em" size="sm" type="email" value={textValue} placeholder={placeholder || (process.env.NEXT_PUBLIC_DEPLOYMENT_ENV !== "production" ? "you@email.com (type test++ for a unique test address)" : "you@email.com")} onChange={e_6 => {
      const next = e_6.target.value;
      if (process.env.NEXT_PUBLIC_DEPLOYMENT_ENV !== "production" && next === "test++") {
        setTextValue(`test+x${Math.round(Math.random() * 100000000000)}@startups.com`);
        return;
      }
      setTextValue(next);
    }} onKeyDown={e_7 => onEnterSubmit(e_7, {
      canSubmit: valueCanSubmit,
      onSubmit: () => void handleValueSubmit()
    })} />;
    $[81] = canSubmit;
    $[82] = fieldType;
    $[83] = handleValueSubmit;
    $[84] = placeholder;
    $[85] = setTextValue;
    $[86] = submitting;
    $[87] = textValue;
    $[88] = valueCanSubmit;
    $[89] = t22;
  } else {
    t22 = $[89];
  }
  let t23;
  if ($[90] !== canSubmit || $[91] !== fieldType || $[92] !== handleValueSubmit || $[93] !== numberValue || $[94] !== placeholder || $[95] !== setNumberValue || $[96] !== submitting || $[97] !== valueCanSubmit) {
    t23 = fieldType === "number" && <Input disabled={!canSubmit || submitting} fontSize="1em" placeholder={placeholder || "0"} size="sm" type="number" value={numberValue} onChange={e_8 => setNumberValue(e_8.target.value)} onKeyDown={e_9 => onEnterSubmit(e_9, {
      canSubmit: valueCanSubmit,
      onSubmit: () => void handleValueSubmit()
    })} />;
    $[90] = canSubmit;
    $[91] = fieldType;
    $[92] = handleValueSubmit;
    $[93] = numberValue;
    $[94] = placeholder;
    $[95] = setNumberValue;
    $[96] = submitting;
    $[97] = valueCanSubmit;
    $[98] = t23;
  } else {
    t23 = $[98];
  }
  let t24;
  if ($[99] !== canSubmit || $[100] !== currency || $[101] !== currencyValue || $[102] !== fieldType || $[103] !== handleValueSubmit || $[104] !== placeholder || $[105] !== setCurrencyValue || $[106] !== submitting || $[107] !== valueCanSubmit) {
    t24 = fieldType === "currency" && <InputGroup startAddon={currency === "USD" ? "$" : currency} width="full"><Input asChild={true} fontSize="1em" size="sm"><CurrencyInput disabled={!canSubmit || submitting} fontSize="1em" numericValue={currencyValue || undefined} placeholder={placeholder || "500,000"} textAlign="start" onNumericValueChange={v_0 => setCurrencyValue(v_0 || 0)} onKeyDown={e_10 => onEnterSubmit(e_10, {
          canSubmit: valueCanSubmit,
          onSubmit: () => void handleValueSubmit()
        })} /></Input></InputGroup>;
    $[99] = canSubmit;
    $[100] = currency;
    $[101] = currencyValue;
    $[102] = fieldType;
    $[103] = handleValueSubmit;
    $[104] = placeholder;
    $[105] = setCurrencyValue;
    $[106] = submitting;
    $[107] = valueCanSubmit;
    $[108] = t24;
  } else {
    t24 = $[108];
  }
  let t25;
  if ($[109] !== canSubmit || $[110] !== fieldType || $[111] !== placeholder || $[112] !== setTagTitles || $[113] !== submitting || $[114] !== tagTitles || $[115] !== tagTypeKeys) {
    t25 = fieldType === "tags" && <Box fontSize="1em" width="full"><ScoQuestionTagsField disabled={!canSubmit || submitting} placeholder={placeholder} selectedTitles={tagTitles} tagTypeKeys={tagTypeKeys} onChange={setTagTitles} /></Box>;
    $[109] = canSubmit;
    $[110] = fieldType;
    $[111] = placeholder;
    $[112] = setTagTitles;
    $[113] = submitting;
    $[114] = tagTitles;
    $[115] = tagTypeKeys;
    $[116] = t25;
  } else {
    t25 = $[116];
  }
  let t26;
  if ($[117] !== fieldType || $[118] !== placeholder || $[119] !== setLocationPlace) {
    t26 = fieldType === "location" && <Box fontSize="1em" width="full"><LocationInput placeholder={placeholder || "City, state, or country"} onChange={place => {
        if (!place) {
          return;
        }
        setLocationPlace(place);
      }} /></Box>;
    $[117] = fieldType;
    $[118] = placeholder;
    $[119] = setLocationPlace;
    $[120] = t26;
  } else {
    t26 = $[120];
  }
  let t27;
  if ($[121] !== acceptRaw || $[122] !== addFile || $[123] !== fieldType || $[124] !== isFull || $[125] !== isUploading || $[126] !== pendingFiles || $[127] !== removeFile || $[128] !== submitting) {
    t27 = fieldType === "file" && <VStack align="stretch" gap={1.5}><HStack><Button {...chipButtonProps} bg="colorPalette.emphasized/15" borderRadius="md" color="colorPalette.fg" disabled={submitting || isFull || isUploading} variant="outline" onClick={() => fileInputRef.current?.click()}><LuPaperclip />{isUploading ? "Uploading\u2026" : "Attach file"}</Button><input ref={fileInputRef} accept={acceptRaw || undefined} style={{
          display: "none"
        }} type="file" onChange={e_11 => {
          const f_0 = e_11.target.files?.[0];
          if (f_0) {
            addFile(f_0);
          }
          if (e_11.target) {
            e_11.target.value = "";
          }
        }} /></HStack><CommentPendingAttachments disabled={submitting} files={pendingFiles} onRemove={removeFile} /></VStack>;
    $[121] = acceptRaw;
    $[122] = addFile;
    $[123] = fieldType;
    $[124] = isFull;
    $[125] = isUploading;
    $[126] = pendingFiles;
    $[127] = removeFile;
    $[128] = submitting;
    $[129] = t27;
  } else {
    t27 = $[129];
  }
  let t28;
  if ($[130] === Symbol.for("react.memo_cache_sentinel")) {
    t28 = {
      bg: "colorPalette.emphasized/90"
    };
    $[130] = t28;
  } else {
    t28 = $[130];
  }
  const t29 = !valueCanSubmit;
  let t30;
  if ($[131] !== handleValueSubmit) {
    t30 = () => void handleValueSubmit();
    $[131] = handleValueSubmit;
    $[132] = t30;
  } else {
    t30 = $[132];
  }
  let t31;
  if ($[133] !== currentUser?.isRegistered || $[134] !== fieldType || $[135] !== isUploading || $[136] !== submitLabel || $[137] !== submitting) {
    t31 = submitting || isUploading ? <Spinner size="xs" /> : fieldType === "email" && !currentUser?.isRegistered ? "Continue with email" : submitLabel;
    $[133] = currentUser?.isRegistered;
    $[134] = fieldType;
    $[135] = isUploading;
    $[136] = submitLabel;
    $[137] = submitting;
    $[138] = t31;
  } else {
    t31 = $[138];
  }
  let t32;
  if ($[139] !== t29 || $[140] !== t30 || $[141] !== t31) {
    t32 = <HStack><Button {...chipButtonProps} _hover={t28} bg="colorPalette.emphasized" borderColor="colorPalette.emphasized" borderRadius="md" color="colorPalette.contrast" disabled={t29} variant="solid" onClick={t30}>{t31}</Button></HStack>;
    $[139] = t29;
    $[140] = t30;
    $[141] = t31;
    $[142] = t32;
  } else {
    t32 = $[142];
  }
  let t33;
  if ($[143] !== t20 || $[144] !== t21 || $[145] !== t22 || $[146] !== t23 || $[147] !== t24 || $[148] !== t25 || $[149] !== t26 || $[150] !== t27 || $[151] !== t32) {
    t33 = <VStack align="stretch" gap={2}>{t20}{t21}{t22}{t23}{t24}{t25}{t26}{t27}{t32}</VStack>;
    $[143] = t20;
    $[144] = t21;
    $[145] = t22;
    $[146] = t23;
    $[147] = t24;
    $[148] = t25;
    $[149] = t26;
    $[150] = t27;
    $[151] = t32;
    $[152] = t33;
  } else {
    t33 = $[152];
  }
  let t34;
  if ($[153] !== props.children) {
    t34 = <chakra.span display="none">{props.children}</chakra.span>;
    $[153] = props.children;
    $[154] = t34;
  } else {
    t34 = $[154];
  }
  return <Box {...cardProps} {...openCardStyle}>{header}{t33}{t34}</Box>;
};
function _temp(s) {
  return s.trim();
}
function _temp2(s_0) {
  return s_0.trim().replace(/^\./, "");
}
function _temp3(o) {
  return o.allowFreeText;
}
async function _temp4(e) {
  console.error("[ScoQuestion] postAnswer failed", e);
}
function _temp5(f) {
  return f.title;
}
function _temp6(label_0, i) {
  return <Badge key={i} size="lg" variant="outline">{label_0}</Badge>;
}